import React from "react";
import { useTranslation } from "react-i18next";

interface QuickSuggestionsProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  visible: boolean;
  isLoading?: boolean;
  enabled?: boolean; // Easy toggle to show/hide the entire component
}

export const QuickSuggestions: React.FC<QuickSuggestionsProps> = ({
  suggestions,
  onSuggestionClick,
  visible,
  isLoading = false,
  enabled = true,
}) => {
  const { t } = useTranslation("ui");
  // Easy way to completely disable the component
  if (!enabled) return null;

  if (!visible) {
    // Return empty div with same height to prevent layout shift
    return <div className="mx-6 mb-0" style={{ height: "75px" }} />;
  }

  return (
    <div
      className="bg-gray-300 border-gray-200 mb-0 rounded-xl border shadow-sm"
      style={{
        height: "75px",
        padding: "8px",
        gap: "8px",
        marginBottom: "-8px",
        zIndex: 10,
        backgroundColor: "#fafafa",
        position: "relative",
      }}
    >
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            height: 6px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.3);
          }

          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
          }
        `}
      </style>
      <h3 className="text-gray-900 mb-2 text-xs font-normal leading-4 tracking-[0.04px]">
        {t("intro.quickSuggestions.askMe")}
      </h3>
      <div
        className="custom-scrollbar flex gap-2 overflow-x-auto"
        style={{
          paddingBottom: "8px",
        }}
      >
        {isLoading
          ? // Skeleton state - 4 skeleton chips with varied widths
            Array.from({ length: 4 }).map((_, index) => {
              const widths = [160, 200, 180, 220]; // Random varied widths
              return (
                <div
                  key={index}
                  className="shrink-0 animate-pulse rounded-md border border-puntt-neutral-gray-8 bg-puntt-neutral-gray-6"
                  style={{
                    height: "24px",
                    padding: "0px 8px",
                    width: `${widths[index]}px`,
                  }}
                />
              );
            })
          : suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick(suggestion)}
                className="flex shrink-0 items-center justify-center whitespace-nowrap transition-colors"
                style={{
                  height: "24px",
                  padding: "0px 8px",
                  background: "rgba(0, 64, 255, 0.0313726)",
                  border: "1px solid rgba(0, 58, 255, 0.3451)",
                  borderRadius: "6px",
                  fontFamily: "National 2",
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0.04px",
                  color: "rgba(0, 12, 220, 0.7412)",
                }}
                data-testid={`quick-suggestion-${index}`}
              >
                {suggestion}
              </button>
            ))}
      </div>
    </div>
  );
};
